import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, ArrowRight, Check, AlertTriangle, User, FileText } from 'lucide-react';
import { ProductSelector } from '../../components/ProductSelector';
import { isInvestmentSelection } from './productOptions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Canonical Canadian provinces/territories — used as a fallback in case the
// reference-data API is slow or temporarily unreachable, so the dropdown is
// NEVER empty.
const PROVINCE_FALLBACK = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

const NewCase = ({ agent, onNavigate, preselectedClientId }) => {
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  // Seed provinces with the fallback so the dropdown is populated on first
  // render even before /api/compliance/reference/provinces resolves.
  const [provinces, setProvinces] = useState(PROVINCE_FALLBACK);
  const [productTypes, setProductTypes] = useState([]);
  const [carriers, setCarriers] = useState([]);

  const [formData, setFormData] = useState({
    client_id: preselectedClientId || '',
    province_of_sale: agent?.province_licensed || 'ON',
    // What does the client want to do? Drives default form selection downstream.
    transaction_type: 'new_coverage',
    // Meeting & intake context — known at first meeting
    meeting_date: new Date().toISOString().slice(0, 10),
    meeting_time: '',
    meeting_mode: 'in_person',
    meeting_initiated_by: 'advisor',
    meeting_language: 'en',
    referral_source: '',
    referrer_name: '',
    primary_need: '',
    // Product details — filled in at Recommendation stage, blank at intake
    product_type: 'term',
    products_applied: [],
    products_other: '',
    carrier: '',
    coverage_amount: 0,
    premium: 0,
    payment_frequency: 'monthly',
    sale_type: 'new',
    has_existing_insurance: false,
    replacement_required: false,
    supervision_required: agent?.supervision_required || false,
    aml_required: false,
    is_investment_product: false,
    supervisor_id: agent?.supervisor_id || ''
  });

  // Iter 35: admin "act on behalf of" agent picker. When the admin opens
  // this page, they must first pick which agent this compliance file is
  // being built FOR. The chosen agent becomes the effective owner of the
  // case + client records below.
  const isAdmin = agent?.id === 'admin';
  const [allAgents, setAllAgents] = useState([]);
  const [onBehalfAgentId, setOnBehalfAgentId] = useState('');
  const onBehalfAgent = isAdmin ? allAgents.find(a => a.id === onBehalfAgentId) : null;
  const effectiveAgent = isAdmin ? onBehalfAgent : agent;

  const fetchReferenceData = useCallback(async () => {
    try {
      const [provRes, prodRes, carrRes] = await Promise.all([
        fetch(`${API_URL}/api/compliance/reference/provinces`),
        fetch(`${API_URL}/api/compliance/reference/product-types`),
        fetch(`${API_URL}/api/compliance/reference/carriers`)
      ]);

      // Only replace the seeded fallback if the API returns a non-empty array,
      // so a slow/failed response never empties the dropdown.
      if (provRes.ok) {
        const data = await provRes.json();
        if (Array.isArray(data) && data.length > 0) setProvinces(data);
      }
      if (prodRes.ok) setProductTypes(await prodRes.json());
      if (carrRes.ok) setCarriers(await carrRes.json());
    } catch (err) {
      console.error('Failed to fetch reference data:', err);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      // Iter 35: scope client list to the effective agent (real agent or
      // on-behalf-of for admin). If admin hasn't picked an agent yet, don't
      // fetch — the picker is shown above.
      if (isAdmin && !effectiveAgent?.id) {
        setClients([]);
        return;
      }
      const url = effectiveAgent?.id
        ? `${API_URL}/api/compliance/clients?advisor_id=${effectiveAgent.id}`
        : `${API_URL}/api/compliance/clients`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setClients(data);

        // If preselected, find the client
        if (preselectedClientId) {
          const client = data.find(c => c.id === preselectedClientId);
          if (client) setSelectedClient(client);
        }
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  }, [effectiveAgent, isAdmin, preselectedClientId]);

  // Iter 35: admin needs to pick which agent to act for — load roster once.
  useEffect(() => {
    if (!isAdmin) return;
    fetch(`${API_URL}/api/agents`)
      .then(r => r.ok ? r.json() : [])
      .then(setAllAgents)
      .catch(err => console.error('Failed to fetch agents roster:', err));
  }, [isAdmin]);

  useEffect(() => {
    fetchReferenceData();
    fetchClients();
  }, [fetchReferenceData, fetchClients]);

  useEffect(() => {
    if (preselectedClientId) {
      setFormData(prev => ({ ...prev, client_id: preselectedClientId }));
    }
  }, [preselectedClientId]);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, client_id: client.id }));
  };

  const handleCreateCase = async () => {
    if (isAdmin && !effectiveAgent?.id) {
      alert('Please select the agent you are creating this file on behalf of (at the top of Step 1).');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/compliance/cases?advisor_id=${effectiveAgent.id}&advisor_name=${encodeURIComponent(effectiveAgent.name)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            coverage_amount: parseFloat(formData.coverage_amount) || 0,
            premium: parseFloat(formData.premium) || 0
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        onNavigate('case-detail', { caseId: data.case.id });
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create case');
      }
    } catch (err) {
      console.error('Failed to create case:', err);
      alert('Failed to create case');
    } finally {
      setLoading(false);
    }
  };

  // Check if product is investment-related
  useEffect(() => {
    const investmentProducts = ['segregated_fund', 'annuity'];
    setFormData(prev => ({
      ...prev,
      is_investment_product: investmentProducts.includes(prev.product_type)
        || isInvestmentSelection(prev.products_applied),
    }));
  }, [formData.product_type, formData.products_applied]);

  // Render steps
  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Iter 35: admin must pick the agent first */}
      {isAdmin && (
        <Card className="bg-amber-50 border-amber-300" data-testid="admin-on-behalf-card">
          <CardContent className="p-4">
            <label className="block text-sm font-semibold text-amber-900 mb-2">
              Acting on behalf of agent <span className="text-red-600">*</span>
            </label>
            <p className="text-xs text-amber-800 mb-2">
              Pick the advisor this compliance file belongs to. The case, client (if new), and
              every form below will be created under their account.
            </p>
            <select
              value={onBehalfAgentId}
              onChange={(e) => {
                setOnBehalfAgentId(e.target.value);
                setSelectedClient(null);
                setFormData(prev => ({ ...prev, client_id: '' }));
              }}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6] bg-white"
              data-testid="admin-on-behalf-agent-select"
            >
              <option value="">— Select agent —</option>
              {allAgents
                .slice()
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.license_number ? ` · LIC ${a.license_number}` : ''}{a.email ? ` · ${a.email}` : ''}
                  </option>
                ))}
            </select>
            {onBehalfAgent && (
              <p className="text-xs text-amber-900 mt-2">
                ✓ Acting as <b>{onBehalfAgent.name}</b>. Their existing clients are listed below.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <h2 className="text-xl font-semibold text-[#003D5C]">Step 1: Select Client</h2>
      
      {selectedClient ? (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white">
                <Check className="h-6 w-6" />
              </div>
              <div>
                <p className="font-semibold text-green-800">{selectedClient.full_name}</p>
                <p className="text-sm text-green-600">{selectedClient.email} • {selectedClient.phone}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => { setSelectedClient(null); setFormData(prev => ({ ...prev, client_id: '' })); }}>
              Change
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <p className="text-gray-600">Select an existing client or create a new one:</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
            {clients.map(client => (
              <Card 
                key={client.id}
                className={`cursor-pointer transition-all hover:shadow-lg ${formData.client_id === client.id ? 'ring-2 ring-[#0077B6]' : ''}`}
                onClick={() => handleClientSelect(client)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#003D5C] rounded-full flex items-center justify-center text-white font-bold">
                      {client.full_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium">{client.full_name}</p>
                      <p className="text-sm text-gray-500">{client.city}, {client.province}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {clients.length === 0 && (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No clients found</p>
            </div>
          )}

          <Button 
            variant="outline" 
            onClick={() => onNavigate('clients')}
            className="w-full"
          >
            + Add New Client
          </Button>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#003D5C]">Step 2: Case Details</h2>

      {/* Transaction Type — what does the client want to do? */}
      <div data-testid="transaction-type-section">
        <label className="block text-sm font-medium mb-2">
          What does the client want to do? <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            { value: 'new_coverage', label: 'New Coverage',  hint: 'First-time purchase' },
            { value: 'replacement',  label: 'Replacement',    hint: 'Replace existing policy' },
            { value: 'upgrade',      label: 'Upgrade',        hint: 'Increase coverage' },
            { value: 'downgrade',    label: 'Downgrade',      hint: 'Reduce coverage' },
            { value: 'cancel',       label: 'Cancel',         hint: 'Cancel existing policy' },
            { value: 'other',        label: 'Other',          hint: 'Service / change / other' },
          ].map(opt => {
            const selected = formData.transaction_type === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormData({
                  ...formData,
                  transaction_type: opt.value,
                  // Auto-toggle replacement flag for convenience
                  replacement_required: opt.value === 'replacement' ? true : formData.replacement_required,
                  has_existing_insurance: ['replacement', 'upgrade', 'downgrade', 'cancel'].includes(opt.value) ? true : formData.has_existing_insurance,
                })}
                className={`border rounded-lg p-3 text-left transition ${selected ? 'border-[#0077B6] bg-[#E6F3F9] ring-2 ring-[#0077B6]/30' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                data-testid={`transaction-type-${opt.value}`}
              >
                <div className={`text-sm font-semibold ${selected ? 'text-[#0077B6]' : 'text-gray-800'}`}>{opt.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{opt.hint}</div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          This drives which compliance forms are pre-selected for the signed package on the next step.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Meeting Date *</label>
          <input
            type="date"
            value={formData.meeting_date || ''}
            onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
            required
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
            data-testid="meeting-date-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Meeting Time</label>
          <input
            type="time"
            value={formData.meeting_time || ''}
            onChange={(e) => setFormData({ ...formData, meeting_time: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
            data-testid="meeting-time-input"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Meeting Mode *</label>
          <div className="grid grid-cols-3 gap-2" data-testid="meeting-mode-group">
            {[
              { value: 'in_person', label: 'In-Person' },
              { value: 'video',     label: 'Video Call' },
              { value: 'phone',     label: 'Phone Call' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormData({ ...formData, meeting_mode: opt.value })}
                className={`border rounded-lg p-2 text-sm transition ${formData.meeting_mode === opt.value ? 'border-[#0077B6] bg-[#E6F3F9] ring-2 ring-[#0077B6]/30 font-semibold text-[#0077B6]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`}
                data-testid={`meeting-mode-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Meeting Initiated By *</label>
          <div className="grid grid-cols-3 gap-2" data-testid="meeting-initiator-group">
            {[
              { value: 'advisor',  label: 'Advisor' },
              { value: 'client',   label: 'Client' },
              { value: 'referrer', label: 'Referrer' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormData({ ...formData, meeting_initiated_by: opt.value })}
                className={`border rounded-lg p-2 text-sm transition ${formData.meeting_initiated_by === opt.value ? 'border-[#0077B6] bg-[#E6F3F9] ring-2 ring-[#0077B6]/30 font-semibold text-[#0077B6]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`}
                data-testid={`meeting-initiator-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Referral Source *</label>
          <select
            value={formData.referral_source || ''}
            onChange={(e) => setFormData({ ...formData, referral_source: e.target.value, referrer_name: ['online', 'walk_in', 'other'].includes(e.target.value) ? '' : formData.referrer_name })}
            required
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
            data-testid="referral-source-select"
          >
            <option value="">Select source…</option>
            <option value="coi">Centre of Influence (COI)</option>
            <option value="friend">Friend</option>
            <option value="relative">Relative</option>
            <option value="colleague">Colleague</option>
            <option value="existing_client">Existing Client</option>
            <option value="online">Online / Website</option>
            <option value="walk_in">Walk-in</option>
            <option value="other">Other</option>
          </select>
        </div>

        {formData.referral_source && !['online', 'walk_in', 'other'].includes(formData.referral_source) && (
          <div>
            <label className="block text-sm font-medium mb-1">Referrer's Name</label>
            <input
              type="text"
              value={formData.referrer_name || ''}
              onChange={(e) => setFormData({ ...formData, referrer_name: e.target.value })}
              placeholder="e.g. John Doe"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
              data-testid="referrer-name-input"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">Province of Sale *</label>
          <select
            value={formData.province_of_sale}
            onChange={(e) => setFormData({ ...formData, province_of_sale: e.target.value })}
            required
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
          >
            {provinces.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Meeting Language</label>
          <div className="grid grid-cols-3 gap-2" data-testid="meeting-language-group">
            {[
              { value: 'en',    label: 'English' },
              { value: 'fr',    label: 'French' },
              { value: 'other', label: 'Other' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormData({ ...formData, meeting_language: opt.value })}
                className={`border rounded-lg p-2 text-sm transition ${formData.meeting_language === opt.value ? 'border-[#0077B6] bg-[#E6F3F9] ring-2 ring-[#0077B6]/30 font-semibold text-[#0077B6]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`}
                data-testid={`meeting-language-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Client's Primary Need (in their own words)</label>
          <textarea
            value={formData.primary_need || ''}
            onChange={(e) => setFormData({ ...formData, primary_need: e.target.value })}
            placeholder="e.g. Protect my family if something happens to me; cover the mortgage; save for retirement"
            rows={2}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
            data-testid="primary-need-input"
          />
          <p className="text-xs text-gray-500 mt-1">Short note — this feeds the Reason-Why letter and helps the Needs Analysis stay client-focused.</p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2">Which product(s) is the client applying for? <span className="text-gray-400 font-normal">(select all that apply)</span></label>
          <ProductSelector
            value={formData.products_applied}
            onChange={(v) => setFormData({ ...formData, products_applied: v })}
            other={formData.products_other}
            onOtherChange={(v) => setFormData({ ...formData, products_other: v })}
            testidPrefix="newcase-product"
          />
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-900 p-3 rounded text-xs" data-testid="step2-info-banner">
        <strong>Product details come later.</strong> Coverage type, carrier, premium and payment frequency are entered in the Recommendation tab once the needs analysis is done.
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#003D5C]">Step 3: Compliance Questions</h2>
      
      <div className="space-y-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Does the client have existing insurance?</p>
              <p className="text-sm text-gray-500">Life, critical illness, disability, or any other coverage</p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={formData.has_existing_insurance ? "default" : "outline"}
                onClick={() => setFormData({ ...formData, has_existing_insurance: true })}
                className={formData.has_existing_insurance ? "bg-[#0077B6]" : ""}
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={!formData.has_existing_insurance ? "default" : "outline"}
                onClick={() => setFormData({ ...formData, has_existing_insurance: false, replacement_required: false })}
                className={!formData.has_existing_insurance ? "bg-[#0077B6]" : ""}
              >
                No
              </Button>
            </div>
          </div>
        </Card>

        {formData.has_existing_insurance && (
          <Card className="p-4 border-yellow-200 bg-yellow-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-yellow-800">Will any existing insurance be replaced, cancelled, reduced, surrendered, lapsed, or materially changed?</p>
                <p className="text-sm text-yellow-600">This triggers the replacement workflow with additional compliance requirements</p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={formData.replacement_required ? "default" : "outline"}
                  onClick={() => setFormData({ ...formData, replacement_required: true })}
                  className={formData.replacement_required ? "bg-yellow-600 hover:bg-yellow-700" : ""}
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={!formData.replacement_required ? "default" : "outline"}
                  onClick={() => setFormData({ ...formData, replacement_required: false })}
                  className={!formData.replacement_required ? "bg-[#0077B6]" : ""}
                >
                  No
                </Button>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Is AML/KYC verification required?</p>
              <p className="text-sm text-gray-500">Anti-money laundering and know-your-client documentation</p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={formData.aml_required ? "default" : "outline"}
                onClick={() => setFormData({ ...formData, aml_required: true })}
                className={formData.aml_required ? "bg-[#0077B6]" : ""}
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={!formData.aml_required ? "default" : "outline"}
                onClick={() => setFormData({ ...formData, aml_required: false })}
                className={!formData.aml_required ? "bg-[#0077B6]" : ""}
              >
                No
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Is the advisor under supervision?</p>
              <p className="text-sm text-gray-500">Supervisor review and approval will be required</p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={formData.supervision_required ? "default" : "outline"}
                onClick={() => setFormData({ ...formData, supervision_required: true })}
                className={formData.supervision_required ? "bg-[#0077B6]" : ""}
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={!formData.supervision_required ? "default" : "outline"}
                onClick={() => setFormData({ ...formData, supervision_required: false })}
                className={!formData.supervision_required ? "bg-[#0077B6]" : ""}
              >
                No
              </Button>
            </div>
          </div>
        </Card>

        {formData.is_investment_product && (
          <Card className="p-4 border-purple-200 bg-purple-50">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <p className="font-medium text-purple-800">Investment Product Detected</p>
                <p className="text-sm text-purple-600">
                  This product ({formData.product_type}) requires additional suitability documentation including 
                  risk tolerance, investment knowledge, time horizon, and fund facts acknowledgement.
                </p>
              </div>
            </div>
          </Card>
        )}

        {formData.replacement_required && (
          <Card className="p-4 border-orange-200 bg-orange-50">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div>
                <p className="font-medium text-orange-800">Replacement Workflow Triggered</p>
                <p className="text-sm text-orange-600">
                  The following additional documents will be required: LIRD, Written Comparative Analysis, 
                  Replacement Rationale, and Client Warning Acknowledgement.
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#003D5C]">Step 4: Review & Create</h2>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Case Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Client</p>
              <p className="font-medium">{selectedClient?.full_name}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">What client wants</p>
              <p className="font-medium capitalize">{(formData.transaction_type || '').replace('_', ' ')}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Meeting</p>
              <p className="font-medium">
                {formData.meeting_date}{formData.meeting_time ? ` · ${formData.meeting_time}` : ''}
                <span className="text-xs text-gray-500 ml-2">({(formData.meeting_mode || '').replace('_', ' ')})</span>
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Initiated By</p>
              <p className="font-medium capitalize">{formData.meeting_initiated_by}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Referral Source</p>
              <p className="font-medium">
                <span className="capitalize">{(formData.referral_source || '').replace('_', ' ')}</span>
                {formData.referrer_name && <span className="text-gray-600"> — {formData.referrer_name}</span>}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Province of Sale</p>
              <p className="font-medium">{provinces.find(p => p.value === formData.province_of_sale)?.label}</p>
            </div>
            {formData.primary_need && (
              <div className="p-3 bg-gray-50 rounded-lg md:col-span-2">
                <p className="text-sm text-gray-500">Client's Primary Need</p>
                <p className="font-medium italic">"{formData.primary_need}"</p>
              </div>
            )}
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-900 p-3 rounded text-xs">
            <strong>Note:</strong> Product type, carrier, coverage amount and premium are entered in the Recommendation tab once the needs analysis is complete.
          </div>

          <div className="border-t pt-4">
            <p className="font-medium mb-2">Compliance Flags:</p>
            <div className="flex flex-wrap gap-2">
              {formData.has_existing_insurance && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">Has Existing Insurance</span>
              )}
              {formData.replacement_required && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">Replacement Required</span>
              )}
              {formData.aml_required && (
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">AML/KYC Required</span>
              )}
              {formData.supervision_required && (
                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">Supervision Required</span>
              )}
              {formData.is_investment_product && (
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">Investment Product</span>
              )}
              {!formData.has_existing_insurance && !formData.replacement_required && !formData.aml_required && 
               !formData.supervision_required && !formData.is_investment_product && (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Standard Case</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="bg-blue-50 p-4 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> After creating this case, a compliance checklist will be automatically generated 
          based on the information provided. You will be able to complete each required document in the case dashboard.
        </p>
      </div>
    </div>
  );

  const canProceed = () => {
    switch (step) {
      case 1: return !!formData.client_id;
      // Step 2 now captures meeting/intake context. Product details are
      // collected later in the Recommendation tab.
      case 2: return !!formData.meeting_date && !!formData.meeting_mode
        && !!formData.meeting_initiated_by && !!formData.referral_source
        && !!formData.province_of_sale && !!formData.transaction_type;
      case 3: return true;
      case 4: return true;
      default: return false;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => onNavigate('dashboard')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#003D5C]">New Insurance Case</h1>
          <p className="text-gray-500">Create a new compliance file</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3, 4].map((s) => (
          <React.Fragment key={s}>
            <div 
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                step === s 
                  ? 'bg-[#0077B6] text-white' 
                  : step > s 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step > s ? <Check className="h-5 w-5" /> : s}
            </div>
            {s < 4 && (
              <div className={`w-16 h-1 ${step > s ? 'bg-green-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="p-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={() => setStep(s => s - 1)}
          disabled={step === 1}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        
        {step < 4 ? (
          <Button 
            onClick={() => setStep(s => s + 1)}
            disabled={!canProceed()}
            className="bg-[#0077B6] hover:bg-[#005A8C]"
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button 
            onClick={handleCreateCase}
            disabled={loading || !canProceed()}
            className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]"
          >
            {loading ? 'Creating...' : 'Create File'}
            <FileText className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default NewCase;
